import type { AddSoftwareResourceToDevicesViewModel, FileIdentifierViewModel } from "@vintecc/capture-api-types/v1";
import type {
  CreateResourceDto,
  EditVersionDto,
  EditResourceDto,
  EditResourceResponseDto,
  CreateVersionResponse,
} from "@vintecc/capture-api-types/v2";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { requests } from "./requests";

import { useToast } from "@vintecc/components";
import { useApiToast } from "@/hooks/apiCall";
import { AxiosResponse } from "axios";
import { useCommonTranslation } from "@/services/culture/common.translations";

import { NewVersionPayload } from "@/modules/resources/types/resourceVersion";

export const queries = {
  getResources: () =>
    queryOptions({
      queryKey: [requests.key],
      queryFn: () => requests.getAll(),
    }),
  getAgenticResources: () =>
    queryOptions({
      queryKey: [requests.key, "agentic"],
      queryFn: () => requests.getAgenticResources(),
      select: (data) => data.resources ?? [],
    }),
  getResource: (id?: number) =>
    queryOptions({
      queryKey: [requests.key, id],
      queryFn: () => requests.getById(id!),
      enabled: !!id,
    }),
  getResourceTypes: () =>
    queryOptions({
      queryKey: [requests.key, "types"],
      queryFn: () => requests.getResourceTypes(),
    }),
  getVersionById: (versionId: number | undefined) =>
    queryOptions({
      queryKey: [requests.key, "version", versionId],
      queryFn: () => requests.getVersionById(versionId!),
      enabled: !!versionId,
    }),
};

export const useMutations = () => {
  const { openToast } = useToast();
  const { t: commonT } = useCommonTranslation();
  const { onSuccess, onError } = useApiToast(requests.key);

  const getFileForDownload = useMutation<{ blob: Blob; filename: string }, unknown, number>({
    mutationFn: (id: number) => requests.getFileForDownload(id),
    onSuccess: () => {
      openToast({
        message: commonT("common.downloadSuccessful"),
        intent: "success",
      });
    },
    onError: (e) => {
      openToast({
        message: String(e),
        intent: "error",
      });
    },
  });
  const deleteResource = useMutation({
    mutationFn: async (id: number) => {
      const res = await requests.delete(id);
      return { data: res } as unknown as AxiosResponse<string>;
    },
    onSuccess,
    onError: (error) => {
      onError(error?.message);
    },
  });
  const deleteResourceVersion = useMutation({
    mutationFn: async (versionId: number) => {
      const res = await requests.deleteVersion(versionId);
      return { data: res } as unknown as AxiosResponse<string>;
    },
    onSuccess,
    onError,
  });
  const createResource = useMutation({
    mutationFn: async (payload: CreateResourceDto) => {
      const res = await requests.create(payload);
      return { data: res } as unknown as AxiosResponse<number>;
    },
    onSuccess,
    onError,
  });

  const editResource = useMutation({
    mutationFn: async (payload: { resourceId: number; payload: EditResourceDto }) => {
      const res = await requests.editResource(payload);
      return { data: res } as unknown as AxiosResponse<EditResourceResponseDto>;
    },
    onSuccess,
    onError,
  });

  const editResourceMarkdown = useMutation({
    mutationFn: async (payload: { id: number; info: string }) => {
      const res = await requests.editResourceMarkdown({ id: payload.id, info: payload.info });
      return { data: res } as unknown as AxiosResponse<string>;
    },
    onSuccess,
    onError,
  });

  const linkEdgeGateways = useMutation({
    mutationFn: async (payload: AddSoftwareResourceToDevicesViewModel) => {
      const res = await requests.linkEdgeGateways(payload);
      return { data: res } as unknown as AxiosResponse<string>;
    },
    onSuccess,
    onError,
  });

  const unlinkEdgeGateway = useMutation({
    mutationFn: async ({ softwareResourceId, deviceId }: { softwareResourceId: number; deviceId: number }) => {
      const res = await requests.unlinkEdgeGateway(softwareResourceId, deviceId);
      return { data: res } as unknown as AxiosResponse<string>;
    },
    onSuccess,
    onError,
  });
  const createResourceVersion = useMutation({
    mutationFn: async (payload: { resourceId: number; payload: NewVersionPayload }) => {
      const res = await requests.createVersion(payload.resourceId, payload.payload);
      return { data: res } as unknown as AxiosResponse<CreateVersionResponse>;
    },
    onSuccess,
    onError,
  });
  const updateResourceVersion = useMutation({
    mutationFn: async (payload: { versionId: number; payload: EditVersionDto }) => {
      const res = await requests.updateVersion(payload.versionId, payload.payload);
      return { data: res } as unknown as AxiosResponse<{ id: number; message?: string }>;
    },
    onSuccess,
    onError,
  });
  const linkResourceToFleets = useMutation({
    mutationFn: async (payload: { resourceId: number; fleetIds: number[] }) => {
      const res = await requests.linkToFleets(payload.resourceId, payload.fleetIds);
      return { data: res } as unknown as AxiosResponse<{ id: number; message?: string }>;
    },
    onSuccess,
    onError,
  });

  const linkResourceToProjects = useMutation({
    mutationFn: async (payload: { resourceId: number; projectIds: number[] }) => {
      const res = await requests.linkToProjects(payload.resourceId, payload.projectIds);
      return { data: res } as unknown as AxiosResponse<{ id: number; message?: string }>;
    },
    onSuccess,
    onError,
  });
  const changeFileIdentifier = useMutation({
    mutationFn: async (payload: FileIdentifierViewModel) => {
      const res = await requests.changeFileIdentifier(payload);
      return { data: res } as unknown as AxiosResponse<{ id: number; message?: string }>;
    },
    onSuccess,
    onError,
  });

  return {
    getFileForDownload,
    deleteResource,
    deleteResourceVersion,
    createResource,
    editResource,
    editResourceMarkdown,
    linkEdgeGateways,
    unlinkEdgeGateway,
    createResourceVersion,
    updateResourceVersion,
    linkResourceToFleets,
    linkResourceToProjects,
    changeFileIdentifier,
  };
};
